import { useMutation, useQuery } from "react-query";
import { AxiosError, AxiosResponse } from "axios";
import HttpService from "../../services/http";
import { queryClient } from "../../providers/QueryProvider";
import {
  TodoBoard,
  TodoBoardSummary,
  TodoColumn,
  TodoBadge,
  TodoTask,
  TodoAccessMode,
  CreateTaskInput,
  UpdateTaskInput,
} from "../../types/TodoTypes";

const boardsKey = (entityId: string) => ["todo-boards", entityId];
export const boardKey = (entityId: string, boardId: string) => [
  "todo-board",
  entityId,
  boardId,
];

const base = (customerId: string, entityId: string) =>
  `/${customerId}/${entityId}/todo`;

const invalidateBoards = (entityId: string) =>
  queryClient.invalidateQueries(boardsKey(entityId));
const invalidateBoard = (entityId: string, boardId: string) => {
  queryClient.invalidateQueries(boardKey(entityId, boardId));
  queryClient.invalidateQueries(boardsKey(entityId));
};

const BOARDS_LIST_QUERY_OPTIONS = {
  cacheTime: 0,
  staleTime: 30_000,
  refetchOnWindowFocus: true,
} as const;

export const TODO_BOARD_POLL_MS = 60_000;

const BOARD_QUERY_OPTIONS = {
  cacheTime: 60_000,
  staleTime: 15_000,
  refetchOnWindowFocus: true,
} as const;

export const useGetTodoBoards = (customerId: string, entityId: string) =>
  useQuery<TodoBoardSummary[], AxiosError<any>>(
    boardsKey(entityId),
    async () => {
      const res = await HttpService.get(`${base(customerId, entityId)}/`);
      return res?.data?.element ?? [];
    },
    {
      enabled: !!customerId && !!entityId,
      keepPreviousData: true,
      ...BOARDS_LIST_QUERY_OPTIONS,
    }
  );

export const isBoardVerdict = (error: AxiosError<any> | null): boolean => {
  const status = error?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
};

export const useGetTodoBoard = (
  customerId: string,
  entityId: string,
  boardId: string,
  { pollingPaused = false }: { pollingPaused?: boolean } = {}
) =>
  useQuery<TodoBoard, AxiosError<any>>(
    boardKey(entityId, boardId),
    async () => {
      const res = await HttpService.get(
        `${base(customerId, entityId)}/${boardId}`
      );
      return res?.data?.element;
    },
    {
      enabled: !!customerId && !!entityId && !!boardId,
      keepPreviousData: true,
      ...BOARD_QUERY_OPTIONS,
      refetchInterval: (_data, query) => {
        if (pollingPaused) return false;
        if (isBoardVerdict(query.state.error)) return false;
        return TODO_BOARD_POLL_MS;
      },
    }
  );

export const useCreateTodoBoard = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { name: string; columns?: TodoColumn[] }
  >((input) => HttpService.post(`${base(customerId, entityId)}/`, input), {
    onSuccess: () => invalidateBoards(entityId),
  });

export const useRenameTodoBoard = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; name: string }
  >(
    ({ boardId, name }) =>
      HttpService.patch(`${base(customerId, entityId)}/${boardId}`, { name }),
    {
      onSettled: (_res, _err, { boardId }) =>
        invalidateBoard(entityId, boardId),
    }
  );

export const useDeleteTodoBoard = (customerId: string, entityId: string) =>
  useMutation<AxiosResponse, AxiosError<any>, string>(
    (boardId) => HttpService.delete(`${base(customerId, entityId)}/${boardId}`),
    { onSettled: () => invalidateBoards(entityId) }
  );

export const useSaveTodoColumns = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; columns: TodoColumn[] }
  >(
    ({ boardId, columns }) =>
      HttpService.put(`${base(customerId, entityId)}/${boardId}/columns`, {
        columns,
      }),
    {
      onSettled: (_res, _err, { boardId }) =>
        invalidateBoard(entityId, boardId),
    }
  );

export const useSaveTodoBadges = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; badges: TodoBadge[] }
  >(
    ({ boardId, badges }) =>
      HttpService.put(`${base(customerId, entityId)}/${boardId}/badges`, {
        badges,
      }),
    {
      onSettled: (_res, _err, { boardId }) =>
        invalidateBoard(entityId, boardId),
    }
  );

export const useSaveTodoBoardAccess = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; mode: TodoAccessMode; user_ids: string[] }
  >(
    ({ boardId, mode, user_ids }) =>
      HttpService.put(`${base(customerId, entityId)}/${boardId}/access`, {
        mode,
        user_ids,
      }),
    {
      onSettled: (_res, _err, { boardId }) =>
        invalidateBoard(entityId, boardId),
    }
  );

export const useCreateTask = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; input: CreateTaskInput }
  >(
    ({ boardId, input }) =>
      HttpService.post(`${base(customerId, entityId)}/${boardId}/task`, input),
    {
      onSettled: (_res, _err, { boardId }) =>
        invalidateBoard(entityId, boardId),
    }
  );

export const useUpdateTask = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; taskId: string; patch: UpdateTaskInput }
  >(
    ({ boardId, taskId, patch }) =>
      HttpService.patch(
        `${base(customerId, entityId)}/${boardId}/task/${taskId}`,
        patch
      ),
    {
      onSuccess: () => invalidateBoards(entityId),
    }
  );

export const useDeleteTask = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; taskId: string }
  >(
    ({ boardId, taskId }) =>
      HttpService.delete(
        `${base(customerId, entityId)}/${boardId}/task/${taskId}`
      ),
    {
      onSettled: (_res, _err, { boardId }) =>
        invalidateBoard(entityId, boardId),
    }
  );

export const useGetTaskAttachment = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; taskId: string; file: string }
  >(({ boardId, taskId, file }) =>
    HttpService.get(
      `${base(customerId, entityId)}/${boardId}/task/${taskId}/attachment`,
      { params: { file } }
    )
  );

export const useDeleteTaskAttachment = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { boardId: string; taskId: string; file: string }
  >(
    ({ boardId, taskId, file }) =>
      HttpService.delete(
        `${base(customerId, entityId)}/${boardId}/task/${taskId}/attachment`,
        { params: { file } }
      ),
    {
      onSettled: (_res, _err, { boardId }) =>
        invalidateBoard(entityId, boardId),
    }
  );

export type { TodoBoard, TodoBoardSummary, TodoTask };
